import type { ReactNode, CSSProperties, HTMLAttributes } from "react";
import { Icon, type IconName } from "./Icon";

export type BadgeTone =
  | "neutral"
  | "soft"
  | "brand"
  | "saffron"
  | "danger"
  | "warn"
  | "info"
  | "success"
  | "outline"
  | "dark"
  | "plum";

export type BadgeSize = "sm" | "md";

const TONES: Record<BadgeTone, string> = {
  neutral: "bg-text/[.06] text-text-2 border-transparent",
  soft: "bg-text/[.04] text-muted border-border",
  brand: "bg-primary-soft text-primary-ink border-transparent",
  saffron: "bg-saffron-soft text-saffron border-transparent",
  danger: "bg-danger-soft text-danger border-transparent",
  warn: "bg-warning-soft text-warning border-transparent",
  info: "bg-info-soft text-info border-transparent",
  success: "bg-primary-soft text-primary-ink border-transparent",
  outline: "bg-transparent text-text-2 border-border-strong",
  dark: "bg-text text-bg border-transparent",
  plum: "bg-plum/15 text-plum border-transparent",
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  size?: BadgeSize;
  icon?: IconName;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  wrap?: boolean;
}

export function Badge({
  tone = "neutral",
  size = "md",
  icon,
  children,
  className = "",
  style,
  wrap = false,
  ...rest
}: BadgeProps) {
  const cls =
    size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]";
  const wrapCls = wrap
    ? "max-w-full min-w-0 items-start whitespace-normal text-left"
    : "items-center whitespace-nowrap";
  return (
    <span
      {...rest}
      style={style}
      className={`inline-flex gap-1 rounded-full font-medium border ${wrapCls} ${TONES[tone]} ${cls} ${className}`}
    >
      {icon && (
        <Icon
          name={icon}
          size={size === "sm" ? 10 : 11}
          className={wrap ? "mt-0.5 shrink-0" : undefined}
        />
      )}
      {wrap ? (
        <span className="min-w-0 break-words">{children}</span>
      ) : (
        children
      )}
    </span>
  );
}
